import React, { useEffect, useState } from "react";
import Footer from "../../../../core/common/Footer";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { z } from "zod";
import useApi from "../../../../core/customhook/useApi";
import Swal from "sweetalert2";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../../utils/getSearchableHeader";
import { filterByColumnSearch } from "../../../utils/filterByColumnSearch";
import Datatable from "../../../../core/common/dataTable";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import "./addlob.css";

const required = "This field is required.";

const departmentSchema = z.object({
  department_code: z.string().trim().min(1, required),
  department_name: z.string().trim().min(1, required),
  description: z.string().trim().min(1, required),
  is_active: z.string().trim().min(1, required),
});

const AddLob = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const programId = location.state?.program_id;

  const [formData, setFormData] = useState({
    department_code: "",
    department_name: "",
    description: "",
    is_active: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [itemList, setItemList] = useState<any[]>([]);
  const [lobList, setLobList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [total, setTotal] = useState(0);

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  const filteredData = filterByColumnSearch(
    Array.isArray(lobList) ? lobList : [],
    inventoryColumnSearch,
  );
  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    if (page === pagination.page && limit === pagination.limit) return;
    const nextPage = limit !== pagination.limit ? 1 : page;
    setPagination({ page: nextPage, limit });
  };

  const createDepartment = useApi<any>("/api/v1/master/departments", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const getProgram = useApi<any>(`/api/v1/master/program/${programId}`, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchPrograms = async () => {
    if (!programId) return;
    try {
      const res = await getProgram.callApi();
      if (res?.success) {
        setItemList(res.data?.rows || []);
        setLobList(res.data?.LOBDetails || []);
        setTotal(res.data?.pagination?.total ?? 0);
      } else {
        setItemList([]);
        setLobList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error(error);
      setItemList([]);
      setLobList([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [programId, pagination.page, pagination.limit]);

  const createLoading = createDepartment.loading;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleAdd = async () => {
    if (!programId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Program Id not found.",
      });
      return;
    }

    const result = departmentSchema.safeParse(formData);

    if (!result.success) {
      const validationErrors: Record<string, string> = {};

      result.error.issues.forEach((issue) => {
        validationErrors[issue.path[0] as string] = issue.message;
      });
      setErrors(validationErrors);
      return;
    }
    fetchPrograms();

    setErrors({});

    try {
      const payload = {
        program_id: programId,
        department_code: formData.department_code,
        department_name: formData.department_name,
        description: formData.description,
        is_active: formData.is_active,
      };

      const res = await createDepartment.callApi(payload);

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res.message || "Department created successfully.",
        });
        fetchPrograms();
        setFormData({
          department_code: "",
          department_name: "",
          description: "",
          is_active: "",
        });
        // navigate(all_routes.lobList);
      }
    } catch (error: any) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong.",
      });
    }
  };
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Program Name",
      dataIndex: "program_name",
      key: "program_name",
    },
    {
      title: "Program Code",
      dataIndex: "program_code",
      key: "program_code",
    },
    {
      title: "Location",
      dataIndex: "location_name",
      key: "location_name",
    },
  ];

  const lobColumns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      width: 100,
      align: "center",
      render: (_: any, __: any, index: number) =>
        (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      title: getSearchableHeader(
        "LOB Code",
        "department_code",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_code",
      key: "department_code",
    },
    {
      title: getSearchableHeader(
        "LOB Name",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
    },
    {
      title: getSearchableHeader(
        "Description",
        "description",
        setInventoryColumnSearch,
      ),
      onCell: () => cellWidth(500),
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: boolean) => (
        <span className={`badge ${value ? "bg-success" : "bg-danger"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];
  const programLoading = getProgram.loading;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Add Line of Business</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item">LoB</li>
                <li className="breadcrumb-item active">Add LoB</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {programLoading ? (
              <SnipperLoader />
            ) : (
              <>
                {/* ================= PROGRAM TABLE ================= */}
                {/* <div className="mb-4">
                  <h5 className="mb-3 p-3 pb-0">Program Details</h5>

                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          {columns.map((col: any) => (
                            <th key={col.key || col.dataIndex}>{col.title}</th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {itemList.length > 0 ? (
                          itemList.map((row: any, rowIndex: number) => (
                            <tr key={row.program_id || rowIndex}>
                              {columns.map((col: any) => (
                                <td key={col.key || col.dataIndex}>
                                  {col.render
                                    ? col.render(
                                        row[col.dataIndex],
                                        row,
                                        rowIndex,
                                      )
                                    : (row[col.dataIndex] ?? "-")}
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={columns.length}
                              className="text-center"
                            >
                              No program data found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div> */}

                {/* ================= PROGRAM DETAILS ================= */}
                <div className="program-overview-wrapper mb-4">
                  <div className="program-overview-header">
                    <div className="d-flex align-items-center gap-3">
                      <div className="program-overview-icon">
                        <i className="ti ti-briefcase fs-3" />
                      </div>

                      <div>
                        <h5 className="mb-1 fw-bold">Program Overview</h5>
                        <p className="mb-0">
                          View the details of your selected program
                        </p>
                      </div>
                    </div>

                    <span className="program-overview-tag">
                      <i className="ti ti-info-circle me-1" />
                      Program Details
                    </span>
                  </div>

                  {itemList.length > 0 ? (
                    itemList.map((row: any, index: number) => (
                      <div
                        className="program-overview-card"
                        key={row.program_id || index}
                      > 
                        <div className="program-info-grid">
                          <div className="program-info-box">
                            <div className="program-info-icon">
                              <i className="ti ti-hash" />
                            </div>

                            <div>
                              <span className="program-label">
                               PROGRAM NAME
                              </span>
                              <h6 className="program-info-value">
                                {row.program_name || "-"}
                              </h6>
                            </div>
                          </div>
                          <div className="program-info-box">
                            <div className="program-info-icon">
                              <i className="ti ti-hash" />
                            </div>

                            <div>
                              <span className="program-label">
                                PROGRAM CODE
                              </span>
                              <h6 className="program-info-value">
                                {row.program_code || "-"}
                              </h6>
                            </div>
                          </div>

                          <div className="program-info-box">
                            <div className="program-info-icon">
                              <i className="ti ti-map-pin" />
                            </div>

                            <div>
                              <span className="program-label">LOCATION</span>
                              <h6 className="program-info-value">
                                {row.location_name || "-"}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="program-empty-state">
                      <i className="ti ti-database-off fs-2 mb-2" />
                      <p className="mb-0">No program data found.</p>
                    </div>
                  )}
                </div>

                <hr />
                <div>
                  <h5 className="p-3 pb-0">Add Line of Business</h5>
                  <div className="row p-3 pb-0">
                    {/* Department Code */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        LOB Code <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        name="department_code"
                        className={`form-control ${
                          errors.department_code ? "is-invalid" : ""
                        }`}
                        placeholder="Enter LOB Code"
                        value={formData.department_code}
                        onChange={handleChange}
                      />

                      {errors.department_code && (
                        <div className="invalid-feedback">
                          {errors.department_code}
                        </div>
                      )}
                    </div>

                    {/* Department Name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        LOB Name <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        name="department_name"
                        className={`form-control ${
                          errors.department_name ? "is-invalid" : ""
                        }`}
                        placeholder="Enter LOB Name"
                        value={formData.department_name}
                        onChange={handleChange}
                      />

                      {errors.department_name && (
                        <div className="invalid-feedback">
                          {errors.department_name}
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Description <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        name="description"
                        className={`form-control ${
                          errors.description ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Description"
                        value={formData.description}
                        onChange={handleChange}
                      />

                      {errors.description && (
                        <div className="invalid-feedback">
                          {errors.description}
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>

                      <select
                        name="is_active"
                        className={`form-select ${
                          errors.is_active ? "is-invalid" : ""
                        }`}
                        value={formData.is_active}
                        onChange={handleChange}
                      >
                        <option value="">Select Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>

                      {errors.is_active && (
                        <div className="invalid-feedback">
                          {errors.is_active}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 p-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAdd}
                      disabled={createLoading}
                    >
                      {createLoading ? "Adding..." : "Add Line of Business"}
                    </button>
                  </div>
                </div>
                <hr />
                <div className="mb-4">
                  <h5 className="p-3 pb-0">Line of Business Details</h5>
                  <Datatable
                    dataSource={filteredData}
                    columns={lobColumns}
                    rowKey="department_id"
                    Selection={false}
                    fixedHeader
                    tableHeight={650}
                    pagination={{
                      current: pagination.page,
                      pageSize: pagination.limit,
                      total,
                    }}
                    handleTableChange={(p: any) =>
                      handleTableChange(p.current, p.pageSize)
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AddLob;
